import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { DateRangePicker, OptionRowsSkeleton, PageHeader } from '@/components/core';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { filterRowsByColumns } from '@/lib/table-query';
import { useInitialPageSize } from '@/hooks/use-server-table';
import { DepartmentFilter } from '@/modules/settings/organization/departments';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import {
  createTimesheetRollupTableColumns,
  rollupTotals,
} from '../../components/timesheet-rollup-table/timesheet-rollup-table-columns';
import {
  useGetMbmProjectsLookup,
  useGetUsersTimesheetRollup,
} from '../../hooks/use-timesheet-manager';
import { ALL_VALUE, useTimesheetFilters } from '../../hooks/use-timesheet-filters';
import { MbmProjectsResult, ResourceBreakdown } from '../../types/timesheet-manager.types';
import { appPath } from '@/constant/app-paths';

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

/**
 * Team Timesheets (HR Module Prototype, Component Map › Team Timesheets rollup): one row per person
 * over a date range, filtered in one toolbar row by name, department, customer and project (Project
 * narrows to the chosen customer), with a totals row. A row opens that person's timesheet with the
 * same range and filters.
 */
export const TimesheetManagerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filters, setFilters, search } = useTimesheetFilters();
  const { range, departmentId, customerId, projectId } = filters;

  const [displayName, setDisplayName] = useState('');
  const initialPageSize = useInitialPageSize(25);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const { getDepartmentIdCondition } = useDepartmentScope();

  const { data: mbmProjectsData, isLoading: isProjectsLoading } = useGetMbmProjectsLookup();
  const mbmProjects = useMemo(
    () => (mbmProjectsData as { MbmProjects?: MbmProjectsResult })?.MbmProjects?.items ?? [],
    [mbmProjectsData]
  );

  const customers = useMemo(() => {
    const map = new Map<string, string>();
    mbmProjects.forEach((project) => map.set(project.CustomerId, project.CustomerName));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [mbmProjects]);

  const projects = useMemo(
    () =>
      customerId === ALL_VALUE
        ? mbmProjects
        : mbmProjects.filter((project) => project.CustomerId === customerId),
    [mbmProjects, customerId]
  );

  const { data, isLoading, error } = useGetUsersTimesheetRollup({
    startDate: range.from,
    endDate: range.to,
    departmentId: getDepartmentIdCondition(departmentId),
    customerId: customerId === ALL_VALUE ? undefined : customerId,
    projectId: projectId === ALL_VALUE ? undefined : projectId,
    displayName: displayName || undefined,
  });
  // Every matching entry is loaded at once and rolled up per employee here, so the table sorts,
  // filters and pages the whole list itself.
  const resources = (data as ResourceBreakdown[]) ?? [];

  const resetPage = useCallback(() => setPagination((prev) => ({ ...prev, pageIndex: 0 })), []);

  // New toolbar filters start over from the first page.
  useEffect(() => {
    resetPage();
  }, [displayName, range, departmentId, customerId, projectId, resetPage]);

  const handlePaginationChange = useCallback((next: { pageIndex: number; pageSize: number }) => {
    setPagination(next);
  }, []);

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      setColumnFilters(updater);
      resetPage();
    },
    [resetPage]
  );

  const columns = createTimesheetRollupTableColumns({ t });
  const shownResources = filterRowsByColumns(resources, columns, columnFilters);

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('TEAM_TIMESHEETS')} />

      <div className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-2 md:flex md:flex-wrap md:items-center">
        <Input
          aria-label={t('EMPLOYEE')}
          placeholder={t('TIMESHEET_MANAGER_SEARCH_BY_NAME')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="h-9 w-full min-[360px]:col-span-2 md:w-48"
        />
        <DateRangePicker
          value={range}
          onChange={(next) => setFilters({ range: next })}
          aria-label={t('DATE_RANGE')}
          className="w-full min-[360px]:col-span-2 md:w-auto"
        />
        <DepartmentFilter
          value={departmentId}
          onChange={(value) => setFilters({ departmentId: value })}
          selectClassName="h-9 w-full md:w-44"
        />
        <Select
          value={customerId}
          onValueChange={(value) => setFilters({ customerId: value, projectId: ALL_VALUE })}
        >
          <SelectTrigger aria-label={t('TIMESHEET_MANAGER_CUSTOMER')} className="h-9 w-full md:w-44">
            <SelectValue placeholder={t('TIMESHEET_MANAGER_CUSTOMER')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>{t('TIMESHEET_MANAGER_ALL_CUSTOMERS')}</SelectItem>
            {isProjectsLoading && <OptionRowsSkeleton rows={3} />}
            {customers.map((customer) => (
              <SelectItem key={customer.id} value={customer.id}>
                {customer.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={projectId} onValueChange={(value) => setFilters({ projectId: value })}>
          <SelectTrigger
            aria-label={t('TIMESHEET_MANAGER_PROJECT')}
            className="h-9 w-full min-[360px]:col-span-2 md:w-44"
          >
            <SelectValue placeholder={t('TIMESHEET_MANAGER_PROJECT')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>{t('TIMESHEET_MANAGER_ALL_PROJECTS')}</SelectItem>
            {isProjectsLoading && <OptionRowsSkeleton rows={3} />}
            {projects.map((project) => (
              <SelectItem key={project.ItemId} value={project.ProjectId}>
                {project.ProjectName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <AdvanceDataTable
        data={resources}
        columns={columns}
        onRowClick={(resource) =>
          navigate(`${appPath.teamTimesheetOf(resource.UserId)}${search ? `?${search}` : ''}`)
        }
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount: shownResources.length,
        }}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        isExpandRowContent={false}
        onPaginationChange={handlePaginationChange}
        mobileView="cards"
        totals={rollupTotals(shownResources)}
      />
    </div>
  );
};
